import React from "react";
import { BrowserRouter, Route, Routes, Navigate, Outlet, useLocation } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles"; // Import ThemeProvider
import theme from "./components/theme";
import Login from "./pages/Login";
import LandingPage from "./pages/LandingPage";
// import "../assets/fonts/font-awesome.min.css";
import MainLayout from "./layout/MainLayout";
import Dashboard from "./pages/Dashboard";
import Staffs from "./pages/Staffs";
import UpdateApplication from "./pages/UpdateApplication";
import AccountActivation from "./pages/AccountActivation";
import ForgotPassword from "./pages/ForgotPassword";
import UserProfile from "./pages/UserProfile";
import Settings from "./pages/Settings";
import UserRole from "./pages/UserRole";
import Invoices from "./pages/Invoices";
import Payments from "./pages/Payments";
import Loans from "./pages/Products";
import Purchases from "./pages/Purchases";
import Reportes from "./pages/Reportes";
import Expenses from "./pages/Expenses";
import PageNotFound from "./pages/PageNotFound";
import Order from "./pages/Order";
import Customers from "./pages/Customers";
import { BranchProvider } from "./contexts/BranchContext";
import { LoanNotificationProvider } from "./contexts/LoanNotificationContext";
import { DividendNotificationProvider } from "./contexts/DividendNotificationContext";
import { PenaltyNotificationProvider } from "./contexts/PenaltyNotificationContext";
import { TransactionNotificationProvider } from "./contexts/TransactionNotificationContext";
import { ExpensesNotificationProvider } from "./contexts/ExpensesNotificationContext";
import Inventories from "./pages/Inventories";
import Branches from "./pages/Branches";
import Sms from "./pages/Sms";
import Forms from "./pages/Forms";
import Announcement from "./pages/Announcement";
import Repayments from "./pages/repayments";
import LoanReport from "./pages/LoanReport";
import GeneralReport from "./pages/GeneralReport";
import Reconciliations from "./pages/Reconciliations";
import CustomerStatements from "./pages/CustomerStatements";
import ProtectedRoute from "./auth/ProtectedRoute";
import AdminDashboard from "./components/dashboard/AdminDashboard";
import AccountantDashboard from "./components/dashboard/AccountantDashboard";
import ChairpersonDashboard from "./components/dashboard/ChairpersonDashboard";
import MemberDashboard from "./components/dashboard/MemberDashboard";
import DeskOfficerDashboard from "./components/dashboard/DeskOfficerDashboard";
import KatibuDashboard from "./components/dashboard/KatibuDashboard";
import UserManagement from "./pages/UserManagement";
import GroupManagement from "./pages/GroupManagement";
import MemberManagement from "./pages/MemberManagement";
import LoanApplication from "./pages/LoanApplication";
import ChairpersonLoanApprovals from "./pages/ChairpersonLoanApprovals";
import KatibuLoanApprovals from "./pages/KatibuLoanApprovals";
import MuhasibuLoanApprovals from "./pages/MuhasibuLoanApprovals";
import PenaltyWaiverRequests from "./pages/PenaltyWaiverRequests";
import WaiverApprovalDashboard from "./pages/WaiverApprovalDashboard";
import MemberContribution from "./pages/MemberContribution";
import MemberCatchUpPayment from "./pages/MemberCatchUpPayment";
import SystemSettings, { AdminSystemSettings } from "./pages/SystemSettings";
import EmergencyContributions from "./pages/EmergencyContributions";
import GroupBusinesses from "./pages/GroupBusinesses";
import SpecialContribution from "./pages/SpecialContributions";
import TransactionsAudit from "./pages/TransactionsAudit";
import ChairpersonCatchUpPayment from "./pages/ChairpersonCatchUpPayment";
import { useAuth } from "./contexts/AuthContext";
import MemberEmergencyContributions from "./pages/MemberEmergencyContributions";
import AdminGroupBusinessApprovals from "./pages/AdminGroupBusinessApprovals";
import SpecialContributionPrograms from "./pages/SpecialContributionPrograms";
import LoanApprovals from "./pages/LoanApprovals";
import MemberDividends from "./pages/MemberDividends";
import LeaderWithdrawalApprovals from "./pages/LeaderWithdrawalApprovals";
import GroupContextSelector from "./pages/GroupContextSelector";
import MigrationOnboarding from "./pages/MigrationOnboarding";
import { MonthlyGroupPerformanceReport } from "./pages/reports/MonthlyGroupPerformanceReport";
import { MemberHealthScoreReport } from "./pages/reports/MemberHealthScoreReport";
import { GroupLiquidityReport } from "./pages/reports/GroupLiquidityReport";
import { SystemGrowthMetricsReport } from "./pages/reports/SystemGrowthMetricsReport";
import { AuditComplianceReport } from "./pages/reports/AuditComplianceReport";
import GroupExpensesPage from "./pages/GroupExpensesPage";
import EntryFeeInvoicePage from "./pages/EntryFeeInvoicePage";
import UpatuDisbursements from "./pages/UpatuDisbursements";
import ReconciliationPage from "./pages/ReconciliationPage";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Terms from "./pages/Terms";
import AccountDeletionPage from "./pages/AccountDeletionPage";
import getDashboardRoute from "./shared/getDashboardRoute";
import {
  getPostAuthRoute,
  getSystemAccessRoute,
  isSystemAccessRemediationPath,
} from "./shared/systemAccessRouting";
import SystemTermsAcceptancePage from "./pages/SystemTermsAcceptancePage";
import SystemFeeAccessPage from "./pages/SystemFeeAccessPage";
function App() {
  const { user: me, role, isAuthenticated, refreshUser } = useAuth();
  const dashboardRoute = getDashboardRoute(me ?? role);
  const postAuthRoute = getPostAuthRoute(me);

  const handleLogin = () => {
    refreshUser();
  };

  const handleLogout = () => { };

  // authorization config
  const hasAccess = (roleArg, allowedRoles) => {
    const candidates = [
      roleArg,
      role,
      me?.group?.name,
      me?.group_name,
    ].filter(Boolean);
    return candidates.some((value) => allowedRoles.includes(value));
  };

  const RequireAuth = () => {
    const location = useLocation();
    if (!isAuthenticated) {
      return <Navigate to="/login" replace />;
    }

    const remediationRoute = getSystemAccessRoute(me);
    const onRemediationPath = isSystemAccessRemediationPath(location.pathname);
    if (remediationRoute && !onRemediationPath) {
      return <Navigate to={remediationRoute} replace />;
    }
    if (!remediationRoute && onRemediationPath) {
      return <Navigate to={postAuthRoute} replace />;
    }
    return <Outlet />;
  };

  return (
    <ExpensesNotificationProvider>
      <LoanNotificationProvider>
        <DividendNotificationProvider>
          <PenaltyNotificationProvider>
            <TransactionNotificationProvider>
              <BranchProvider>
                <ThemeProvider theme={theme}>
                  <BrowserRouter>
                    <Routes>
                      {/* Public-facing experience (landing, auth, policy) */}
                      <Route path="/" element={<LandingPage />} />
                      <Route
                        path="/login"
                        element={
                          isAuthenticated ? (
                            <Navigate to={postAuthRoute} replace />
                          ) : (
                            <Login
                              onLogin={handleLogin}
                              isAuthenticated={isAuthenticated}
                            />
                          )
                        }
                      />

                      <Route path="/forgot-password" element={<ForgotPassword />} />
                      <Route
                        path="/account-activation/:activationCode?"
                        element={<AccountActivation />}
                      />
                      <Route path="/privacy" element={<PrivacyPolicy />} />
                      <Route path="/terms" element={<Terms />} />
                      <Route path="/account-deletion" element={<AccountDeletionPage />} />

                      {/* Authenticated system (all /app/*) */}
                      <Route path="/app" element={<RequireAuth />}>
                        <Route index element={<Navigate to={dashboardRoute} replace />} />
                        <Route
                          path="my-groups"
                          element={
                            <GroupContextSelector />
                          }
                        />
                        <Route path="system-access/terms" element={<SystemTermsAcceptancePage />} />
                        <Route path="system-access/fees" element={<SystemFeeAccessPage />} />

                        <Route
                          path="general-report"
                          element={
                            <MainLayout
                              onLogout={handleLogout}
                              activePage={<GeneralReport />}
                            ></MainLayout>
                          }
                        />
                        <Route
                          path="update-application/:id"
                          element={
                            <MainLayout
                              onLogout={handleLogout}
                              activePage={<UpdateApplication />}
                            ></MainLayout>
                          }
                        />
                        <Route
                          path="customers"
                          element={
                            <MainLayout
                              onLogout={handleLogout}
                              activePage={<Customers />}
                            ></MainLayout>
                          }
                        />
                        <Route
                          path="reconciliations"
                          element={
                            <ProtectedRoute
                              element={() => (
                                <MainLayout
                                  onLogout={handleLogout}
                                  activePage={<Reconciliations />}
                                />
                              )}
                              isAuthenticated={isAuthenticated}
                              hasAccess={hasAccess(role, [
                                "Admin",
                                "General Manager",
                                "Cashier",
                                "Loan Officer",
                                "Branch Manager",
                              ])}
                              requiredRoles={[
                                "Admin",
                                "General Manager",
                                "Cashier",
                                "Loan Officer",
                                "Branch Manager",
                              ]}
                              redirectTo={dashboardRoute}
                            />
                          }
                        />
                        <Route
                          path="forms"
                          element={
                            <ProtectedRoute
                              element={() => (
                                <MainLayout
                                  onLogout={handleLogout}
                                  activePage={<Forms />}
                                />
                              )}
                              isAuthenticated={isAuthenticated}
                              hasAccess={hasAccess(role, [
                                "Admin",
                                "General Manager",
                              ])}
                              requiredRoles={[
                                "Admin",
                                "General Manager",
                              ]}
                              redirectTo={dashboardRoute}
                            />
                          }
                        />
                        <Route
                          path="admin/migrations"
                          element={
                            <ProtectedRoute
                              element={() => (
                                <MainLayout
                                  onLogout={handleLogout}
                                  activePage={<MigrationOnboarding />}
                                />
                              )}
                              isAuthenticated={isAuthenticated}
                              hasAccess={hasAccess(role, ["Admin"])}
                              requiredRoles={["Admin"]}
                              redirectTo={dashboardRoute}
                            />
                          }
                        />
                        <Route
                          path="admin/reconciliation"
                          element={
                            <ProtectedRoute
                              element={() => (
                                <MainLayout
                                  onLogout={handleLogout}
                                  activePage={<ReconciliationPage />}
                                />
                              )}
                              isAuthenticated={isAuthenticated}
                              hasAccess={hasAccess(role, ["Admin", "Mwenyekiti", "Chairperson"])}
                              requiredRoles={["Admin", "Mwenyekiti", "Chairperson"]}
                              redirectTo={dashboardRoute}
                            />
                          }
                        />
                        <Route
                          path="announcement"
                          element={
                            <ProtectedRoute
                              element={() => (
                                <MainLayout
                                  onLogout={handleLogout}
                                  activePage={<Announcement />}
                                />
                              )}
                              isAuthenticated={isAuthenticated}
                              hasAccess={hasAccess(role, [
                                "Admin",
                                "General Manager",
                              ])}
                              requiredRoles={[
                                "Admin",
                                "General Manager",
                              ]}
                              redirectTo={dashboardRoute}
                            />
                          }
                        />
                        <Route
                          path="branches"
                          element={
                            <ProtectedRoute
                              element={() => (
                                <MainLayout
                                  onLogout={handleLogout}
                                  activePage={<Branches />}
                                />
                              )}
                              isAuthenticated={isAuthenticated}
                              hasAccess={hasAccess(role, ["Admin"])}
                              redirectTo={dashboardRoute}
                            />
                          }
                        />
                        <Route
                          path="admin/dashboard"
                          element={
                            <MainLayout
                              onLogout={handleLogout}
                              activePage={<AdminDashboard />}
                            ></MainLayout>
                          }
                        />
                        <Route
                          path="accountant/dashboard"
                          element={
                            <MainLayout
                              onLogout={handleLogout}
                              activePage={<AccountantDashboard />}
                            ></MainLayout>
                          }
                        />
                        <Route
                          path="chairperson/dashboard"
                          element={
                            <MainLayout
                              onLogout={handleLogout}
                              activePage={<ChairpersonDashboard />}
                            ></MainLayout>
                          }
                        />
                        <Route
                          path="member/dashboard"
                          element={
                            <MainLayout
                              onLogout={handleLogout}
                              activePage={<MemberDashboard />}
                            ></MainLayout>
                          }
                        />
                        <Route
                          path="deskofficer/dashboard"
                          element={
                            <MainLayout
                              onLogout={handleLogout}
                              activePage={<DeskOfficerDashboard />}
                            ></MainLayout>
                          }
                        />
                        <Route
                          path="katibu/dashboard"
                          element={
                            <MainLayout
                              onLogout={handleLogout}
                              activePage={<KatibuDashboard />}
                            ></MainLayout>
                          }
                        />
                        <Route
                          path="customer-statements/:customerId"
                          element={
                            <MainLayout
                              onLogout={handleLogout}
                              activePage={<CustomerStatements />}
                            ></MainLayout>
                          }
                        />
                        <Route
                          path="repayments/:loanId?"
                          element={
                            <MainLayout
                              onLogout={handleLogout}
                              activePage={<Repayments />}
                            ></MainLayout>
                          }
                        />
                        <Route
                          path="dashboard"
                          element={
                            <Navigate to={dashboardRoute} replace />
                          }
                        />
                        <Route
                          path="users"
                          element={
                            <ProtectedRoute
                              element={() => (
                                <MainLayout
                                  onLogout={handleLogout}
                                  activePage={<Staffs />}
                                />
                              )}
                              isAuthenticated={isAuthenticated}
                              hasAccess={hasAccess(role, ["Admin"])}
                              redirectTo={dashboardRoute}
                            />
                          }
                        />
                        <Route
                          path="loans/:customerId?"
                          element={
                            <MainLayout
                              onLogout={handleLogout}
                              activePage={<Loans />}
                            ></MainLayout>
                          }
                        />
                        <Route
                          path="loan-report"
                          element={
                            <MainLayout
                              onLogout={handleLogout}
                              activePage={<LoanReport />}
                            ></MainLayout>
                          }
                        />
                        <Route
                          path="reports/monthly-group-performance"
                          element={
                            <MainLayout
                              onLogout={handleLogout}
                              activePage={<MonthlyGroupPerformanceReport />}
                            ></MainLayout>
                          }
                        />
                        <Route
                          path="reports/member-health-score"
                          element={
                            <MainLayout
                              onLogout={handleLogout}
                              activePage={<MemberHealthScoreReport />}
                            ></MainLayout>
                          }
                        />
                        <Route
                          path="reports/group-liquidity"
                          element={
                            <MainLayout
                              onLogout={handleLogout}
                              activePage={<GroupLiquidityReport />}
                            ></MainLayout>
                          }
                        />
                        <Route
                          path="reports/system-growth-metrics"
                          element={
                            <MainLayout
                              onLogout={handleLogout}
                              activePage={<SystemGrowthMetricsReport />}
                            ></MainLayout>
                          }
                        />
                        <Route
                          path="reports/audit-compliance"
                          element={
                            <MainLayout
                              onLogout={handleLogout}
                              activePage={<AuditComplianceReport />}
                            ></MainLayout>
                          }
                        />
                        <Route
                          path="order"
                          element={
                            <MainLayout
                              onLogout={handleLogout}
                              activePage={<Order />}
                            ></MainLayout>
                          }
                        />
                        <Route
                          path="inventories"
                          element={
                            <ProtectedRoute
                              element={() => (
                                <MainLayout
                                  onLogout={handleLogout}
                                  activePage={<Inventories />}
                                />
                              )}
                              isAuthenticated={isAuthenticated}
                              hasAccess={hasAccess(role, [
                                "General Manager",
                                "Branch Manager",
                                "Admin",
                              ])}
                              redirectTo={dashboardRoute}
                            />
                          }
                        />
                        <Route
                          path="roles"
                          element={
                            <MainLayout
                              onLogout={handleLogout}
                              activePage={<UserRole />}
                            ></MainLayout>
                          }
                        />
                        <Route
                          path="purchases"
                          element={
                            <MainLayout
                              onLogout={handleLogout}
                              activePage={<Purchases />}
                            ></MainLayout>
                          }
                        />
                        <Route
                          path="reports"
                          element={
                            <MainLayout
                              onLogout={handleLogout}
                              activePage={<Reportes />}
                            ></MainLayout>
                          }
                        />
                        <Route
                          path="expenses"
                          element={
                            <MainLayout
                              onLogout={handleLogout}
                              activePage={<GroupExpensesPage />}
                            ></MainLayout>
                          }
                        />
                        <Route
                          path="invoices"
                          element={
                            <MainLayout
                              onLogout={handleLogout}
                              activePage={<Invoices />}
                            ></MainLayout>
                          }
                        />
                        <Route
                          path="entry-fee/invoice"
                          element={
                            <MainLayout
                              onLogout={handleLogout}
                              activePage={<EntryFeeInvoicePage />}
                            ></MainLayout>
                          }
                        />
                        <Route
                          path="payments"
                          element={
                            <MainLayout
                              onLogout={handleLogout}
                              activePage={<Payments />}
                            ></MainLayout>
                          }
                        />
                        <Route
                          path="user-profile"
                          element={
                            <MainLayout
                              onLogout={handleLogout}
                              activePage={<UserProfile />}
                            ></MainLayout>
                          }
                        />
                        <Route
                          path="admin/users"
                          element={
                            <MainLayout
                              onLogout={handleLogout}
                              activePage={<UserManagement />}
                            ></MainLayout>
                          }
                        />
                        <Route
                          path="desk-officer/groups"
                          element={
                            <MainLayout
                              onLogout={handleLogout}
                              activePage={<GroupManagement />}
                            ></MainLayout>
                          }
                        />
                        <Route
                          path="admin/groups"
                          element={
                            <MainLayout
                              onLogout={handleLogout}
                              activePage={<GroupManagement />}
                            ></MainLayout>
                          }
                        />
                        <Route
                          path="chairperson/members"
                          element={
                            <MainLayout
                              onLogout={handleLogout}
                              activePage={<MemberManagement />}
                            ></MainLayout>
                          }
                        />
                        <Route
                          path="member/loan/apply"
                          element={
                            <MainLayout
                              onLogout={handleLogout}
                              activePage={<LoanApplication />}
                            ></MainLayout>
                          }
                        />
                        <Route
                          path="chairperson/loan-approvals"
                          element={
                            <MainLayout
                              onLogout={handleLogout}
                              activePage={<ChairpersonLoanApprovals />}
                            ></MainLayout>
                          }
                        />
                        <Route
                          path="chairperson/emergency-contributions"
                          element={
                            <MainLayout
                              onLogout={handleLogout}
                              activePage={<EmergencyContributions />}
                            ></MainLayout>
                          }
                        />
                        <Route
                          path="member/emergency-contributions"
                          element={
                            <MainLayout
                              onLogout={handleLogout}
                              activePage={<MemberEmergencyContributions />}
                            ></MainLayout>
                          }
                        />
                        <Route
                          path="member/special-contributions/create"
                          element={
                            <MainLayout
                              onLogout={handleLogout}
                              activePage={<SpecialContribution />}
                            ></MainLayout>
                          }
                        />
                        <Route
                          path="member/special-contributions"
                          element={
                            <MainLayout
                              onLogout={handleLogout}
                              activePage={<SpecialContributionPrograms />}
                            ></MainLayout>
                          }
                        />
                        <Route
                          path="finance/transactions-audit"
                          element={
                            <MainLayout
                              onLogout={handleLogout}
                              activePage={<TransactionsAudit />}
                            ></MainLayout>
                          }
                        />
                        <Route
                          path="chairperson/group-businesses"
                          element={
                            <MainLayout
                              onLogout={handleLogout}
                              activePage={<GroupBusinesses />}
                            ></MainLayout>
                          }
                        />
                        <Route
                          path="admin/group-businesses"
                          element={
                            <MainLayout
                              onLogout={handleLogout}
                              activePage={<AdminGroupBusinessApprovals />}
                            ></MainLayout>
                          }
                        />
                        <Route
                          path="chairperson/catchup-payments"
                          element={
                            <MainLayout
                              onLogout={handleLogout}
                              activePage={<ChairpersonCatchUpPayment />}
                            ></MainLayout>
                          }
                        />
                        <Route
                          path="accountant/loan-approvals"
                          element={
                            <MainLayout
                              onLogout={handleLogout}
                              activePage={<MuhasibuLoanApprovals />}
                            ></MainLayout>
                          }
                        />
                        <Route
                          path="loan-approvals"
                          element={
                            <MainLayout
                              onLogout={handleLogout}
                              activePage={<LoanApprovals />}
                            ></MainLayout>
                          }
                        />
                        <Route
                          path="member/penalty-waiver"
                          element={
                            <MainLayout
                              onLogout={handleLogout}
                              activePage={<PenaltyWaiverRequests />}
                            ></MainLayout>
                          }
                        />
                        <Route
                          path="viongozi/penalty-waiver"
                          element={
                            <MainLayout
                              onLogout={handleLogout}
                              activePage={<WaiverApprovalDashboard />}
                            ></MainLayout>
                          }
                        />
                        <Route
                          path="leadership/member-withdrawals"
                          element={
                            <MainLayout
                              onLogout={handleLogout}
                              activePage={<LeaderWithdrawalApprovals />}
                            ></MainLayout>
                          }
                        />
                        <Route
                          path="upatu/disbursements"
                          element={
                            <MainLayout
                              onLogout={handleLogout}
                              activePage={<UpatuDisbursements />}
                            ></MainLayout>
                          }
                        />
                        <Route
                          path="member/contributions"
                          element={
                            <MainLayout
                              onLogout={handleLogout}
                              activePage={<MemberContribution />}
                            ></MainLayout>
                          }
                        />
                        <Route
                          path="member/catchup-payments"
                          element={
                            <MainLayout
                              onLogout={handleLogout}
                              activePage={<MemberCatchUpPayment />}
                            ></MainLayout>
                          }
                        />
                        <Route
                          path="member/dividends"
                          element={
                            <MainLayout
                              onLogout={handleLogout}
                              activePage={<MemberDividends />}
                            ></MainLayout>
                          }
                        />
                        <Route
                          path="admin/settings"
                          element={
                            <MainLayout
                              onLogout={handleLogout}
                              activePage={<AdminSystemSettings />}
                            ></MainLayout>
                          }
                        />
                        <Route
                          path="chairperson/system-settings"
                          element={
                            <MainLayout
                              onLogout={handleLogout}
                              activePage={<SystemSettings />}
                            ></MainLayout>
                          }
                        />
                        <Route
                          path="katibu/loan-approvals"
                          element={
                            <MainLayout
                              onLogout={handleLogout}
                              activePage={<KatibuLoanApprovals />}
                            ></MainLayout>
                          }
                        />
                        <Route
                          path="settings"
                          element={
                            <MainLayout
                              onLogout={handleLogout}
                              activePage={<Settings />}
                            ></MainLayout>
                          }
                        />
                        <Route
                          path="sms"
                          element={
                            <ProtectedRoute
                              element={() => (
                                <MainLayout
                                  onLogout={handleLogout}
                                  activePage={<Sms />}
                                />
                              )}
                              isAuthenticated={isAuthenticated}
                              hasAccess={hasAccess(role, [
                                "Admin",
                                "General Manager",
                              ])}
                              requiredRoles={[
                                "Admin",
                                "General Manager",
                              ]}
                              redirectTo={dashboardRoute}
                            />
                          }
                        />
                        <Route index element={<Navigate to="dashboard" replace />} />
                      </Route>
                      <Route path="*" element={<PageNotFound />} />
                    </Routes>

                  </BrowserRouter>
                </ThemeProvider>
              </BranchProvider>
            </TransactionNotificationProvider>
          </PenaltyNotificationProvider>
        </DividendNotificationProvider>
      </LoanNotificationProvider>
    </ExpensesNotificationProvider>
  );
}

export default App;
